/* Service-worker registration helper for CARTAL.
 *
 * Behaviour:
 *  - Skips registration during local CRA dev (npm start).
 *  - Registers /sw.js on load in deployed builds.
 *  - On a new SW being installed, sends SKIP_WAITING so the fresh
 *    service-worker activates immediately, then reloads the page once.
 *  - Also wipes all caches every time the **app version string** changes
 *    (synced with sw.js) so stale chunks from old releases never linger.
 */

const SW_PATH = "/sw.js";
const APP_VERSION = "cartal-v266-iter266-marketing-mode";
const VERSION_KEY = "cartal:app-version";

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Skip in dev (CRA on localhost) to avoid caching surprises while iterating
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.startsWith("192.168.");
  if (isLocalhost && process.env.NODE_ENV !== "production") return;

  // ── One-shot client-side cleanup whenever APP_VERSION advances ─────
  // Forces returning visitors past the previous SW lock by nuking caches
  // and unregistering the OLD service-worker (so the new sw.js v2 takes
  // over on the next navigation).
  try {
    const last = window.localStorage.getItem(VERSION_KEY);
    if (last && last !== APP_VERSION) {
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
      }
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister().catch(() => {}));
        // Reload once to pick up the fresh bundle from the network
        if (!sessionStorage.getItem("cartal:reloaded")) {
          sessionStorage.setItem("cartal:reloaded", "1");
          window.localStorage.setItem(VERSION_KEY, APP_VERSION);
          window.location.reload();
          return;
        }
      }).catch(() => {});
    }
    window.localStorage.setItem(VERSION_KEY, APP_VERSION);
  } catch {
    /* ignore quota / private-mode errors */
  }

  // Defer until after page load so it doesn't block initial paint
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_PATH)
      .then((reg) => {
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              // New version detected — activate it immediately
              installing.postMessage?.({ type: "SKIP_WAITING" });
              // Reload exactly once to swap to the fresh assets
              if (!sessionStorage.getItem("cartal:swReloaded")) {
                sessionStorage.setItem("cartal:swReloaded", "1");
                setTimeout(() => window.location.reload(), 300);
              }
              window.dispatchEvent(new CustomEvent("cartal:sw-update", { detail: reg }));
            }
          });
        });
      })
      .catch(() => {
        /* silently ignore registration errors */
      });
  });
}

export function unregisterServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
}
